import { B2Button } from "@hdb/biome-2";
import { FC, createRef, useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import * as yup from "yup";
import pdfIcon from "../../../assets/images/pdfBiomeIcon.svg";
import spreadsheetIcon from "../../../assets/images/spreadsheetIconBiome.svg";
import HasPermission from "../../../core/guards/has-permission";
import CustomHighChartComponent from "../../../shared/custom-highchart-component/custom-highchart.component";
import CustomSpinnerComponent from "../../../shared/custom-spinner/custom-spinner.component";
import FilterFormComponent, {
  FilterFormConfig,
} from "../../../shared/filter-form/filter-form.component";
import { downloadBurnTimeReportsFile } from "../../../store/slices/fault-module/burn-time-report.slice";
import { getBlocksBySiteCode } from "../../../store/slices/location-block.slice";
import { getSiteByTCCode } from "../../../store/slices/location-site.slice";
import { getTownCouncils } from "../../../store/slices/town-council.slice";

import moment from "moment";
import { generateFilenameWithTimestamp } from "src/shared/helpers/generateFilenameWithTimestamp";
import { assetsByBlock } from "src/store/slices/asset.slice";
interface BurnTimeReportTableComponentProps {
  reportData: any;
  onRefetchData: (
    tcCode: any,
    siteCode: any,
    blockCode: any,
    reportType: any,
    startDate: any,
    endDate: any,
    asset: any
  ) => void;
  isLoading: boolean;
}

const BurnTimeReportTableComponent: FC<BurnTimeReportTableComponentProps> = ({
  reportData,
  onRefetchData,
  isLoading,
}) => {
  const formRef = createRef();
  const [filterUpdateCount, setFilterUpdateCount] = useState(0);
  const [tcOptions, setTCOptions] = useState<any>([]);
  const [siteOptions, setSiteOptions] = useState<any>([]);
  const [blockOptions, setBlockOptions] = useState<any>([]);
  const [selectedReportType, setSelectedReportType] = useState<any>(null);
  const [selectedAssetCode, setSelectedAssetCode] = useState<any>(null);
  const [isFiltered, setIsFiltered] = useState<any>(false);
  const [blockChangeDetected, setBlockChangeDetected] = useState(0);
  const [appliedReportType, setAppliedReportType] = useState<any>(null);
  const [downloaDocumentPayload, setDownloaDocumentPayload] = useState<any>({
    townCouncil: "",
    site: "",
    block: "",
    reportType: "",
    startDate: "",
    endDate: "",
    asset: "",
  });
  const [assetOptions, setAssetOptions] = useState<any>([]);
  const [selectedBlock, setSelectedBlock] = useState<any>(null);
  const [isAssetSearch, setIsAssetSearch] = useState<boolean>(true);
  const [selectedTC, setSelectedTC] = useState<any>(null);
  const [selectedSite, setSelectedSite] = useState<any>(null);
  const [chartData, setChartData] = useState<{
    sensorIds: [];
    burnTime: [];
    extraData: [];
  }>({ sensorIds: [], burnTime: [], extraData: [] });

  const reportTypeOptions = [
    {
      label: "Burn Time",
      value: "1",
    },
    {
      label: "Burn Time (%)",
      value: "2",
    },
  ];

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getTownCouncils({ PageNumber: 0, pageSize: 0 }));
  }, [dispatch]);

  const townCouncilData = useSelector((state: any) => {
    return state.townCouncil.townCouncils;
  });

  const isLoadingTownCouncils = useSelector((state: any) => {
    return state.townCouncil.isLoadingTownCouncils;
  });

  const siteDataByTCCode = useSelector((state: any) => {
    return state.locationSite.sitesByTCCode;
  });

  const assetsData = useSelector((state: any) => {
    return state.asset.selectedAssetsByBlock;
  });

  const isLocationSitesByTCCode = useSelector((state: any) => {
    return state.locationSite.isLocationSitesByTCCode;
  });

  const blockDataSiteCode = useSelector((state: any) => {
    return state.locationBlock.blocksBySiteCode;
  });

  const isLoadingBlocksBySiteCode = useSelector((state: any) => {
    return state.locationBlock.isLoadingBlocksBySiteCode;
  });

  const isLoadingBurnTimeReportsDownload = useSelector((state: any) => {
    return state.burnTimeReport.isLoadingBurnTimeReportsDownload;
  });

  useEffect(() => {
    if (reportData?.data?.length) {
      if (selectedAssetCode) {
        // const burnTime = reportData?.data?.map((d: any) => d?.burnTime);

        const burnTime = reportData?.data?.every(
          (d: any) => d?.burnTime == null
        )
          ? null
          : reportData?.data?.map((d: any) => d?.burnTime);

        const extraData = reportData?.data?.map((d: any) => {
          return {
            code: d?.assetCode,
            displayName: d?.sensorDisplayName,
            level: d?.level,
          };
        });

        const burnTimeDates = reportData?.data?.map((d: any) =>
          moment(d?.timeStamp).format("DD-MM-YYYY")
        );

        setChartData({
          sensorIds: burnTimeDates,
          burnTime,
          extraData: extraData || [],
        });
      } else {
        const filteredData = reportData?.data?.filter(
          (item: any) => item.burnTime != null
        );

        const sensorIds = filteredData?.map((d: any) => d?.sensorDisplayName);

        // const burnTime = filteredData?.map((d: any) => d?.burnTime);
        const burnTime = filteredData?.every((d: any) => d?.burnTime == null)
          ? null
          : filteredData?.map((d: any) => d?.burnTime);

        const extraData = filteredData?.map((d: any) => {
          return {
            code: d?.assetCode,
            displayName: d?.sensorDisplayName,
            level: d?.level,
          };
        });

        setChartData({
          sensorIds,
          burnTime,
          extraData: extraData || [],
        });
      }
    } else {
      setChartData({
        sensorIds: [],
        burnTime: [],
        extraData: [],
      });
    }
  }, [reportData]);

  useEffect(() => {
    const assetIDs =
      assetsData?.map(
        (asset: { assetCode: string; sensorDisplayName: string }) => ({
          label: asset.sensorDisplayName,
          value: asset.assetCode,
        })
      ) || [];

    if (assetIDs.length > 0) {
      assetIDs.unshift({
        label: "All",
        value: null,
      });
    }

    setAssetOptions(assetIDs);

    if (assetsData?.length > 0) {
      setIsAssetSearch(false);
    }
  }, [assetsData]);

  // useEffect(() => {
  //   const assetIDs: string[] = assetsData?.map(
  //     (asset: { assetCode: string; sensorDisplayName: string }) => ({
  //       label: asset.sensorDisplayName,
  //       value: asset.assetCode,
  //     })
  //   );

  //   setAssetOptions(assetIDs);

  //   if (assetsData.length > 0) {
  //     setIsAssetSearch(false);
  //   }
  // }, [assetsData]);

  const [currentTC, setCurrentTC] = useState({ label: "", value: "" });
  useEffect(() => {
    const tcs = townCouncilData?.data?.map((tc: any) => ({
      label: tc?.name,
      value: tc?.tcCode,
    }));

    // if user is TC Admin only 1 tc is available
    if (tcs?.length === 1) {
      setCurrentTC(tcs[0]);
    }

    setTCOptions(tcs);
    setFilterUpdateCount(filterUpdateCount + 1);
  }, [townCouncilData]);

  useEffect(() => {
    if (currentTC.value.length) {
      setFormFilterInitialValues((prev) => ({
        ...prev,
        townCouncil: currentTC.value,
      }));

      dispatch(getSiteByTCCode({ tcCode: currentTC.value }));
    }
  }, [currentTC]);

  useEffect(() => {
    const sites = siteDataByTCCode?.data?.map((site: any) => ({
      label: site?.name,
      value: site?.siteCode,
    }));

    if (!sites?.length) return;

    setSiteOptions(sites);
  }, [siteDataByTCCode]);

  useEffect(() => {
    const blocks = blockDataSiteCode?.data?.map((block: any) => ({
      label: block?.blockName,
      value: block?.blockCode,
    }));

    setBlockOptions(blocks);
  }, [blockDataSiteCode]);

  useEffect(() => {
    if (selectedTC && selectedSite && selectedBlock) {
      dispatch(assetsByBlock({ blockCode: selectedBlock }));
    }
  }, [selectedBlock]);

  useEffect(() => {
    setSiteOptions([]);
    setBlockOptions([]);
  }, []);

  const onChangeTC = (selectedTC: any) => {
    setSiteOptions([]);
    setBlockOptions([]);
    setAssetOptions([]);

    setSelectedTC(selectedTC);
    setBlockChangeDetected(blockChangeDetected + 1);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      townCouncil: selectedTC,
      site: "",
      block: "",
      asset: "",
    }));

    if (!selectedTC) return;
    dispatch(getSiteByTCCode({ tcCode: selectedTC }));
  };

  const onChangeSite = (selectedSite: any) => {
    setBlockOptions([]);
    setAssetOptions([]);
    setSelectedSite(selectedSite);
    setBlockChangeDetected(blockChangeDetected + 1);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      site: selectedSite,
      block: "",
      asset: "",
    }));
    if (!selectedSite) return;

    dispatch(getBlocksBySiteCode({ siteCode: selectedSite }));
  };

  const onChangeBlock = (selectedBlock: any) => {
    setAssetOptions([]);
    setBlockChangeDetected(blockChangeDetected + 1);
    setSelectedBlock(selectedBlock);
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      block: selectedBlock,
      asset: "",
    }));
  };

  const onChangeAsset = (selectedAsset: any) => {
    setSelectedAssetCode(selectedAsset);

    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      asset: selectedAsset,
    }));
  };

  const onChangeReportType = (reportType: any) => {
    setSelectedReportType(reportType);
    setSelectedAssetCode(null);
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      reportType: reportType,
      asset: "",
      startDate: "",
      endDate: "",
    }));
  };

  const onChangeStartDate = (startDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      startDate: startDate,
    }));
  };

  const onChangeEndDate = (endDate: any) => {
    setFormFilterInitialValues((prevInitValues: any) => ({
      ...prevInitValues,
      endDate: endDate,
    }));
  };

  useEffect(() => {
    return () => {
      setSelectedReportType(null);
    };
  }, []);

  const onSubmitFilter = () => {
    const form: any = formRef.current;
    if (form) {
      form.handleSubmit();

      const {
        townCouncil,
        site,
        block,
        reportType,
        startDate,
        endDate,
        asset,
      } = form.values;

      if (endDate) {
        if (endDate < startDate) {
          setIsFiltered(false);
          return;
        }
      }

      if (reportType && townCouncil && site && block) {
        if (reportType == 1 && !startDate) {
          setIsFiltered(false);
          return;
        }

        setAppliedReportType(reportType);

        setSelectedReportType(reportType);
      } else {
        setIsFiltered(false);
        return;
      }

      onRefetchData(
        townCouncil ? townCouncil : null,
        site ? site : null,
        block ? block : null,
        reportType ? reportType : null,
        selectedReportType == 1 && startDate ? moment.utc(startDate) : null,
        selectedReportType == 1 && endDate && selectedAssetCode
          ? moment.utc(endDate)
          : null,
        selectedReportType == 1 && asset ? asset : null
      );

      setDownloaDocumentPayload({
        townCouncil,
        site,
        block,
        reportType,
        startDate,
        endDate: endDate && selectedAssetCode ? moment.utc(endDate) : null,
        asset,
      });

      setIsFiltered(true);
    }
  };

  const onClickDownload = (fileType: any) => {
    const form: any = formRef.current;

    if (form) {
      // form.handleSubmit();

      const {
        townCouncil,
        site,
        block,
        reportType,
        startDate,
        endDate,
        asset,
      } = downloaDocumentPayload;

      if (appliedReportType && townCouncil && site && block) {
        if (reportType == 1 && !startDate) return;

        setAppliedReportType(reportType);
        // setSelectedReportType(reportType);
      } else {
        setIsFiltered(false);
        return;
      }

      dispatch(
        downloadBurnTimeReportsFile({
          fileType,
          tcCode: townCouncil ? townCouncil : null,
          siteCode: site ? site : null,
          blockCode: block ? block : null,
          reportType: reportType ? reportType : null,
          startDate:
            reportType == 1 && startDate ? moment.utc(startDate) : null,
          endDate:
            reportType == 1 && endDate && asset
              ? moment.utc(endDate)
              : null,
          asset: reportType == 1 && asset ? asset : null,
        })
      );
    }
  };

  const [formFilterInitialValues, setFormFilterInitialValues] = useState({
    townCouncil: "",
    site: "",
    block: "",
    asset: "",
    reportType: "",
    startDate: "",
    endDate: "",
  });

  const onReset = () => {
    const form: any = formRef.current;
    form.resetForm();

    setIsFiltered(false);
    setSelectedReportType(null);
    setAppliedReportType(null);

    setSiteOptions([]);
    setBlockOptions([]);
    setFilterUpdateCount(filterUpdateCount + 1);
    setSelectedAssetCode(null);

    setIsAssetSearch(true);
    setSelectedBlock(null);
    setFormFilterInitialValues({
      townCouncil: "",
      site: "",
      block: "",
      asset: "",
      reportType: "",
      startDate: "",
      endDate: "",
    });
  };

  const filterActionConfig: any = {
    label: "Search",
    event: onSubmitFilter,
    variant: "primary",
  };

  const reset: any = {
    label: "Reset",
    event: onReset,
    variant: "secondary",
  };

  const date13MonthsAgo = moment().subtract(13, "months").format("YYYY-MM-DD");
  const yesterdayDateTime = moment().subtract(1, "day").format("YYYY-MM-DD");

  const [filterFormConfig, setFilterFormConfig] = useState<FilterFormConfig>({
    controls: [],
  });

  useEffect(() => {
    setFilterFormConfig({ controls: [] });

    const controls = [
      {
        controlGroup: [
          {
            name: "townCouncil",
            placeholder: "Select Town Council *",
            type: "searchable-select",
            options: tcOptions,
            onChange: onChangeTC,
            required: true,
            isLoading: isLoadingTownCouncils,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "site",
            placeholder: "Select Site *",
            type: "searchable-select",
            options: siteOptions,
            onChange: onChangeSite,
            required: true,
            isLoading: isLocationSitesByTCCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "block",
            placeholder: "Select Block *",
            type: "searchable-select",
            options: blockOptions,
            onChange: onChangeBlock,
            required: true,
            isLoading: isLoadingBlocksBySiteCode,
          },
        ],
      },
      {
        controlGroup: [
          {
            name: "reportType",
            placeholder: "Select Report Type *",
            type: "select",
            onChange: onChangeReportType,
            options: reportTypeOptions,
            required: true,
            isLoading: false,
          },
        ],
      },
    ];

    let ontrolsWhenRerportTypeSelected: any = [];
    let controlsWhenAssetCodeSelected: any = [];

    if (selectedReportType == 1) {
      ontrolsWhenRerportTypeSelected = [
        {
          controlGroup: [
            {
              name: "asset",
              placeholder: "All Assets",
              type: "searchable-select",
              required: false,
              selectType: "single",
              options: assetOptions,
              isLoading: true,
              disabled: isAssetSearch,
              onChange: onChangeAsset,
              resetSearchableSelect: blockChangeDetected,
            },
          ],
        },
        {
          controlGroup: [
            {
              name: "startDate",
              placeholder:
                selectedReportType == 1 && selectedAssetCode
                  ? "Select Start Date *"
                  : "Select Date *",
              type: "date",
              onChange: onChangeStartDate,
              required: true,
              isLoading: false,
              minDate: date13MonthsAgo,
              maxDate: yesterdayDateTime,
            },
          ],
        },
      ];
    }

    if (selectedReportType == 1 && selectedAssetCode) {
      controlsWhenAssetCodeSelected = [
        {
          controlGroup: [
            {
              name: "endDate",
              placeholder: "Select End Date",
              type: "date",
              onChange: onChangeEndDate,
              required: true,
              isLoading: false,
              minDate: date13MonthsAgo,
              maxDate: yesterdayDateTime,
            },
          ],
        },
      ];
    }

    const updatedControls = [
      ...controls,
      ...ontrolsWhenRerportTypeSelected,
      ...controlsWhenAssetCodeSelected,
    ];

    setFilterFormConfig((prevConfig) => {
      const prevControlsJson = JSON.stringify(prevConfig.controls);
      const newControlsJson = JSON.stringify(updatedControls);

      if (prevControlsJson !== newControlsJson) {
        return { controls: updatedControls };
      }

      return prevConfig;
    });
  }, [
    tcOptions,
    siteOptions,
    blockOptions,
    assetOptions,
    // reportTypeOptions,
    selectedAssetCode,
    selectedReportType,
  ]);

  useEffect(() => {
    setSiteOptions([]);
    setBlockOptions([]);
    setAssetOptions([]);
    setIsAssetSearch(true);
  }, []);

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  const validationSchema = yup.object().shape({
    reportType: yup.number().required("Report Type is required"),
    townCouncil: yup.string().required("Town Council is required"),
    site: yup.string().required("Site is required"),
    block: yup.string().required("Block is required"),
    startDate: yup.date().required("Start Date is required"),
    endDate: yup
      .date()
      .min(yup.ref("startDate"), "End Date must be after Start Date"),
  });

  const burnTimeChartToolTipFormatter = (point: any, extraData?: any) => {
    const { x, y } = point;
    const xAxisPropertyName =
      chartData?.sensorIds?.[point.index] || point.category || x;

    const xAxisLabel = selectedAssetCode ? "Time (Date)" : "Asset";
    const yAxisLabel =
      Number(appliedReportType) === 1 ? "Burn Time (hours)" : "Burn Time(%)";

    return `<b>${xAxisLabel}: </b> ${xAxisPropertyName}<br/><b>${yAxisLabel}: </b> ${y} <br/>Level: ${extraData?.find((data: any) => data?.displayName == point.category)
      ?.level || "Not Assigned"
      }`;
  };

  return (
    <div>
      {/*       <div className="flex justify-end table-generic-action mb-2">
        <B2Button
          className="icon-text-btn"
          variant="primary"
          onClick={redirectToConfiguration}
        >
          Automatic Report Scheduler
          <FontAwesomeIcon icon={faCalendar} className="fs-7 ms-2" />
        </B2Button>
      </div>
 */}
      <div>
        <FilterFormComponent
          key={formFilterInitialValues?.townCouncil}
          formRef={formRef}
          initialValues={formFilterInitialValues}
          formConfig={filterFormConfig}
          actionConfig={filterActionConfig}
          resetButton={reset}
          validation={validationSchema}
          filterUpdateCount={filterUpdateCount}
        />
      </div>

      <div>
        <CustomSpinnerComponent
          height={"400px"}
          width={"100%"}
          isLoading={isLoading}
        >
          {appliedReportType && isFiltered && (
            <div className="my-3">
              <div className="export pt-4 d-flex align-items-center justify-content-end">
                <HasPermission
                  moduleName="Burn Time Report"
                  actionName="Download-Burn Time Report"
                  isRoute={false}
                >
                  <div className="d-flex gap-2">
                    <B2Button
                      variant="light"
                      onClick={() => onClickDownload("downloadPdf")}
                      disabled={!chartData?.burnTime?.length}
                    >
                      <img className="fs-7 me-2" src={pdfIcon} alt="PDF Icon" />
                      {" "}Export as PDF
                    </B2Button>

                    <B2Button
                      variant="light"
                      onClick={() => onClickDownload("downloadExcel")}
                      disabled={!chartData?.burnTime?.length}
                    >
                      <img
                        className="fs-7 me-2"
                        src={spreadsheetIcon}
                        alt="Spreadsheet Icon"
                      />{" "}
                      Export as Excel
                    </B2Button>
                  </div>

                  {isLoadingBurnTimeReportsDownload ? (
                    <Spinner
                      className="ms-2"
                      style={{ color: "#1976D2" }}
                      animation="border"
                      role="status"
                      size="sm"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                  ) : null}
                </HasPermission>
              </div>
              {
                appliedReportType == 1 ? (
                  <CustomHighChartComponent
                    chartTitle="Burn Time"
                    xAxisCategories={chartData?.sensorIds}
                    SeriesType={selectedAssetCode ? "line" : "column"}
                    seriesData={chartData?.burnTime}
                    seriesShowInLegend={false}
                    seriesName={"Burn Time"}
                    xAxisTitleText={
                      selectedAssetCode ? "Time (Date)" : "Assets"
                    }
                    yAxisTitleText={"Burn Time (Hours)"}
                    colors={["#66b2ff"]}
                    extraData={chartData?.extraData}
                    redirectRoute="/asset/"
                    // baseLine={[40000]}
                    fileName={generateFilenameWithTimestamp("Burn Time")}
                    chartName={"Burn Time"}
                    tooltipFormatter={burnTimeChartToolTipFormatter}
                  />
                ) : appliedReportType == 2 ? (
                  <CustomHighChartComponent
                    chartTitle="Burn Time ( based on 50,000 hours )"
                    xAxisCategories={chartData?.sensorIds}
                    SeriesType={selectedAssetCode ? "line" : "column"}
                    seriesData={chartData?.burnTime}
                    seriesShowInLegend={false}
                    seriesName={"Burn Time ( % )"}
                    xAxisTitleText={
                      selectedAssetCode ? "Time (Date)" : "Assets"
                    }
                    yAxisTitleText={"Burn Time(%)"}
                    colors={["#66b2ff"]}
                    extraData={chartData?.extraData}
                    redirectRoute="/asset/"
                    // baseLine={[50]}
                    fileName={generateFilenameWithTimestamp(
                      "Burn Time ( based on 50,000 hours )"
                    )}
                    chartName={"Burn Time ( based on 50,000 hours )"}
                    tooltipFormatter={burnTimeChartToolTipFormatter}
                  />
                ) : null /* (
                <CustomHighChartComponent
                  chartTitle="Burn Time(50,000 hour)"
                  xAxisCategories={chartData?.sensorIds}
                  SeriesType={selectedAssetCode ? "line" : "column"}
                  seriesData={chartData?.burnTime}
                  seriesShowInLegend={false}
                  seriesName={"Burn Time(50,000 hour)"}
                  xAxisTitleText={selectedAssetCode ? "Time (Date)" : "Assets"}
                  yAxisTitleText={"Burn Time(50,000 hour)"}
                  colors={["#3B1DCD"]}
                  extraData={chartData?.extraData}
                  redirectRoute="/asset/"
                  // baseLine={[75000]}
                />
              ) */
              }
            </div>
          )}
        </CustomSpinnerComponent>
      </div>
    </div>
  );
};

export default BurnTimeReportTableComponent;
